import { useState, useCallback, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { UniqueIdentifier } from "@dnd-kit/core";
import { toast } from "sonner";
import { Board, useBoardDragHandlers, springs } from "@/components/ui";
import type { ColumnData, BoardDragResult } from "@/components/ui";
import type { AuditStatus, UIAudit, AuditId } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import { AuditCard } from "./AuditCard";
import {
  getAuditColumns,
  isValidStatusTransition,
  getActionForTransition,
} from "./constants";
import { useAuditStatusLabels } from "./useAuditStatusLabels";
import { useAuditTransitionLabels } from "./useAuditTransitionLabels";
import { useAuditStatusChange } from "./use-audit-status-change";
import { AuditStatusConfirmModal } from "./AuditStatusConfirmModal";
import { AuditRejectModal } from "./AuditRejectModal";
import { BoardEmptyColumn } from "@/components/ui/Board/BoardEmptyColumn";

interface AuditBoardViewProps {
  audits: UIAudit[];
  onCardClick: (id: string) => void;
}

interface AuditColumn {
  id: AuditStatus;
  title: string;
  audits: UIAudit[];
}

interface PendingTransition {
  auditId: AuditId;
  sourceStatus: AuditStatus;
  targetStatus: AuditStatus;
  actionLabel: string;
  targetStatusLabel: string;
}

function findAuditById(
  columns: AuditColumn[],
  auditId: UniqueIdentifier,
): UIAudit | undefined {
  for (const col of columns) {
    const audit = col.audits.find((a) => String(a.id) === String(auditId));
    if (audit) return audit;
  }
  return undefined;
}

function findColumnById(
  columns: AuditColumn[],
  columnId: UniqueIdentifier,
): AuditColumn | undefined {
  return columns.find((column) => String(column.id) === String(columnId));
}

function findColumnByCardId(
  columns: AuditColumn[],
  cardId: UniqueIdentifier,
): AuditColumn | undefined {
  return columns.find((column) =>
    column.audits.some((audit) => String(audit.id) === String(cardId)),
  );
}

function resolveColumnIdFromDndId(
  columns: AuditColumn[],
  id: UniqueIdentifier | null | undefined,
): AuditStatus | null {
  if (!id) return null;

  const directColumn = findColumnById(columns, id);
  if (directColumn) return directColumn.id;

  const cardColumn = findColumnByCardId(columns, id);
  return cardColumn?.id ?? null;
}

export function AuditBoardView({ audits, onCardClick }: AuditBoardViewProps) {
  const { t } = useTranslation("audits");
  const statusLabels = useAuditStatusLabels();
  const transitionLabels = useAuditTransitionLabels();
  const [columns, setColumns] = useState<AuditColumn[]>(() =>
    getAuditColumns(audits),
  );
  const [pendingTransition, setPendingTransition] =
    useState<PendingTransition | null>(null);

  const preDragColumnsRef = useRef<AuditColumn[] | null>(null);
  const dragSourceColumnRef = useRef<AuditStatus | null>(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const { execute, isPending } = useAuditStatusChange();

  // Sync columns when audits change — skip while a confirmation modal is open
  useEffect(() => {
    if (pendingTransition) return;
    setColumns(getAuditColumns(audits));
  }, [audits, pendingTransition]);

  const columnData: ColumnData[] = columns.map((col) => ({
    id: col.id,
    cardIds: col.audits.map((a) => a.id),
  }));

  const handleMove = useCallback((result: BoardDragResult) => {
    const sourceColumn = findColumnById(
      columnsRef.current,
      result.sourceColumnId,
    );
    const targetColumn = findColumnById(
      columnsRef.current,
      result.targetColumnId,
    );

    if (!sourceColumn || !targetColumn) return;

    const sourceStatus = sourceColumn.id;
    const targetStatus = targetColumn.id;

    // Same-column reorder — always allow
    if (sourceStatus === targetStatus) {
      setColumns((prev) => {
        const next = prev.map((col) => ({ ...col, audits: [...col.audits] }));
        const col = next.find((c) => c.id === sourceStatus);
        if (!col) return prev;
        const [moved] = col.audits.splice(result.oldIndex, 1);
        col.audits.splice(result.newIndex, 0, moved);
        return next;
      });
      return;
    }

    // Cross-column: block invalid transitions
    if (!isValidStatusTransition(sourceStatus, targetStatus)) {
      return;
    }

    // Valid cross-column move — update visual state
    setColumns((prev) => {
      const next = prev.map((col) => ({ ...col, audits: [...col.audits] }));
      const sourceCol = next.find((c) => c.id === sourceStatus);
      const targetCol = next.find((c) => c.id === targetStatus);
      if (!sourceCol || !targetCol) return prev;
      const [moved] = sourceCol.audits.splice(result.oldIndex, 1);
      targetCol.audits.splice(result.newIndex, 0, moved);
      return next;
    });
  }, []);

  const { activeId, handleDragStart, handleDragOver, handleDragEnd } =
    useBoardDragHandlers({
      columns: columnData,
      onMove: handleMove,
    });

  const wrappedHandleDragStart = useCallback(
    (event: Parameters<typeof handleDragStart>[0]) => {
      preDragColumnsRef.current = columnsRef.current.map((col) => ({
        ...col,
        audits: [...col.audits],
      }));

      // Find which column the card starts in
      const sourceColumn = findColumnByCardId(
        columnsRef.current,
        event.active.id,
      );
      dragSourceColumnRef.current = sourceColumn?.id ?? null;

      handleDragStart(event);
    },
    [handleDragStart],
  );

  const wrappedHandleDragEnd = useCallback(
    (event: Parameters<typeof handleDragEnd>[0]) => {
      const originalColumn = dragSourceColumnRef.current;

      // Detect what column the user dropped over (before dnd-kit processes it)
      const attemptedColumn = resolveColumnIdFromDndId(
        columnsRef.current,
        event.over?.id,
      );

      handleDragEnd(event);

      const movedAudit = findAuditById(columnsRef.current, event.active.id);
      if (!movedAudit) {
        preDragColumnsRef.current = null;
        dragSourceColumnRef.current = null;
        return;
      }

      // Find the card's current column after the drop
      const currentColumn = findColumnByCardId(
        columnsRef.current,
        event.active.id,
      )?.id;

      // Check if the user attempted an invalid cross-column move
      if (
        originalColumn &&
        attemptedColumn &&
        attemptedColumn !== originalColumn &&
        !isValidStatusTransition(originalColumn, attemptedColumn)
      ) {
        const sourceLabel = statusLabels[originalColumn].label;
        const targetLabel = statusLabels[attemptedColumn].label;
        toast.error(
          t("board.cannotMove", { source: sourceLabel, target: targetLabel }),
        );
        if (preDragColumnsRef.current) {
          setColumns(preDragColumnsRef.current);
        }
        preDragColumnsRef.current = null;
        dragSourceColumnRef.current = null;
        return;
      }

      // No cross-column move — clean up
      if (
        !originalColumn ||
        !currentColumn ||
        originalColumn === currentColumn
      ) {
        preDragColumnsRef.current = null;
        dragSourceColumnRef.current = null;
        return;
      }

      const action = getActionForTransition(originalColumn, currentColumn);

      // No backend action — revert
      if (!action) {
        if (preDragColumnsRef.current) {
          setColumns(preDragColumnsRef.current);
        }
        preDragColumnsRef.current = null;
        dragSourceColumnRef.current = null;
        return;
      }

      // Valid transition — show confirmation modal
      const transitionKey = `${originalColumn}->${currentColumn}`;
      setPendingTransition({
        auditId: movedAudit.id,
        sourceStatus: originalColumn,
        targetStatus: currentColumn,
        actionLabel: transitionLabels[transitionKey]?.label ?? "",
        targetStatusLabel: statusLabels[currentColumn].label,
      });

      dragSourceColumnRef.current = null;
    },
    [handleDragEnd, t, statusLabels, transitionLabels],
  );

  function revertToPredrag() {
    if (preDragColumnsRef.current) {
      setColumns(preDragColumnsRef.current);
    }
    preDragColumnsRef.current = null;
    setPendingTransition(null);
  }

  async function handleConfirm() {
    if (!pendingTransition) return;
    const action = getActionForTransition(
      pendingTransition.sourceStatus,
      pendingTransition.targetStatus,
    );
    if (!action) {
      revertToPredrag();
      return;
    }

    try {
      await execute(action, pendingTransition.auditId, {
        targetStatus: pendingTransition.targetStatus,
      });
      preDragColumnsRef.current = null;
      setPendingTransition(null);
    } catch {
      revertToPredrag();
    }
  }

  async function handleRejectConfirm(reason: string) {
    if (!pendingTransition) return;
    try {
      await execute("reject", pendingTransition.auditId, {
        reason,
      });
      preDragColumnsRef.current = null;
      setPendingTransition(null);
    } catch {
      revertToPredrag();
    }
  }

  const activeAudit = activeId ? findAuditById(columns, activeId) : null;

  const showConfirmModal =
    pendingTransition !== null && pendingTransition.targetStatus !== "rejected";
  const showRejectModal =
    pendingTransition !== null && pendingTransition.targetStatus === "rejected";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="flex-1 overflow-hidden min-h-0"
    >
      <Board
        onDragStart={wrappedHandleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={wrappedHandleDragEnd}
      >
        {columns.map((col, colIndex) => {
          const statusConfig = statusLabels[col.id];

          return (
            <motion.div
              key={col.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springs.smooth, delay: colIndex * 0.06 }}
              className="h-full min-h-0 flex flex-col rounded-xl p-3"
              style={{
                backgroundColor: `${statusConfig.dotColor}08`,
                border: `1px solid ${statusConfig.dotColor}18`,
              }}
            >
              <Board.Column id={col.id} cardIds={col.audits.map((a) => a.id)}>
                {/* Column header: title + animated count */}
                <div className="flex items-center gap-2 px-1 pb-3">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: statusConfig.dotColor }}
                  />
                  <h2 className="text-sm font-medium text-secondary">
                    {statusConfig.label}
                  </h2>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={col.audits.length}
                      className="text-base text-secondary"
                      initial={{ y: -8, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 8, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      ({col.audits.length})
                    </motion.span>
                  </AnimatePresence>
                </div>

                {/* Cards */}
                <div className="flex flex-col gap-4 overflow-y-auto pr-1 pb-2 scrollbar-thin w-72 flex-1 min-h-0">
                  {col.audits.map((audit) => (
                    <Board.Card key={audit.id} id={audit.id}>
                      <AuditCard audit={audit} onClick={onCardClick} />
                    </Board.Card>
                  ))}
                  {col.audits.length === 0 && (
                    <BoardEmptyColumn visible columnId={col.id} />
                  )}
                </div>
              </Board.Column>
            </motion.div>
          );
        })}

        <Board.DragOverlay>
          {activeAudit ? (
            <div className="w-72">
              <AuditCard audit={activeAudit} />
            </div>
          ) : null}
        </Board.DragOverlay>

        <Board.Minimap
          columns={columns.map((col) => ({
            id: col.id,
            color: statusLabels[col.id].dotColor,
            cardCount: col.audits.length,
          }))}
        />
      </Board>

      <AuditStatusConfirmModal
        open={showConfirmModal}
        onClose={revertToPredrag}
        onConfirm={handleConfirm}
        actionLabel={pendingTransition?.actionLabel ?? ""}
        targetStatusLabel={pendingTransition?.targetStatusLabel ?? ""}
        isPending={isPending}
      />

      <AuditRejectModal
        open={showRejectModal}
        onClose={revertToPredrag}
        onConfirm={handleRejectConfirm}
        isPending={isPending}
      />
    </motion.div>
  );
}
